// /inventory/activity — full audit log of every inventory event.
//
// Reached from the 查看全部 link on the /inventory compact feed and from
// the sidebar (库存 group). Shows up to 200 most recent events, filterable
// by SKU, location, and event kind. Same timeline visual as the compact
// feed, just unlimited.
//
// Backend: GET /inventory/events?limit=200&product_id=&location=  (already
// exists from Phase 2a — we just bump the limit and surface the filter UI).

import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api.ts';
import { ActivityItem } from '../components/ActivityFeed.jsx';
import { LOCATIONS, locationLabel } from '../lib/locations.ts';

// 活动日志 is operational — shows the real kitchen (CK_RH / CK_NY), not the
// summed CK, so you can see WHERE an event happened.
const KINDS = [
  { key: 'produced',    label: '生产' },
  { key: 'sold',        label: '售出' },
  { key: 'wasted',      label: '报废' },
  { key: 'transferred', label: '调拨' },
  { key: 'adjusted',    label: '调整' }
];

export const InventoryActivity = () => {
  const [events, setEvents] = useState(null);
  const [products, setProducts] = useState([]);
  const [productFilter, setProductFilter] = useState('');     // product_id or ''
  const [locationFilter, setLocationFilter] = useState('');   // location or ''
  const [kindFilter, setKindFilter] = useState(new Set());    // Set of kinds to include
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Tab title — restores Regina's bookmarking context.
  useEffect(() => {
    document.title = '活动日志 · VAVA';
    return () => { document.title = 'VAVA Designer Cakes'; };
  }, []);

  async function load() {
    setLoading(true); setErr('');
    try {
      const opts = { limit: 200 };
      if (productFilter)  opts.product_id = productFilter;
      if (locationFilter) opts.location   = locationFilter;
      const ev = await api.listInventoryEvents(opts);
      setEvents(ev);
    } catch (e) {
      setErr(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, [productFilter, locationFilter]);

  useEffect(() => {
    api.listProducts({ includeInactive: true }).then(setProducts).catch(() => {});
  }, []);

  function toggleKind(k) {
    setKindFilter(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  // Apply client-side filters: kind set + free-text search across actor + product name
  const filtered = useMemo(() => {
    let rows = events || [];
    if (kindFilter.size > 0) rows = rows.filter(e => kindFilter.has(e.kind));
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(e =>
        (e.actorName || '').toLowerCase().includes(q) ||
        (e.productName || '').toLowerCase().includes(q) ||
        (e.location || '').toLowerCase().includes(q) ||
        (e.reason || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [events, kindFilter, search]);

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">活动日志</h1>
          <div className="vava-page-meta">
            完整库存事件记录 · 谁在什么时候做了什么 · 最多 200 条最近事件
          </div>
        </div>
      </div>

      <div className="inv-toolbar">
        <select className="inv-sort"
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}>
          <option value="">所有 SKU</option>
          {products.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select className="inv-sort"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}>
          <option value="">所有门店</option>
          {LOCATIONS.map(l => <option key={l} value={l}>{locationLabel(l)}</option>)}
        </select>
        <div className="inv-segmented" role="group" aria-label="事件类型">
          {KINDS.map(k => (
            <button key={k.key}
                    aria-pressed={kindFilter.has(k.key)}
                    aria-selected={kindFilter.has(k.key)}
                    onClick={() => toggleKind(k.key)}>
              {k.label}
            </button>
          ))}
        </div>
        <div className="inv-search">
          <input type="text"
                 value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 placeholder="搜 人 / SKU / 门店 / 原因…" />
        </div>
        <span className="vava-hint" style={{ marginLeft: 'auto' }}>
          {filtered.length} 条
        </span>
      </div>

      {err && <div className="att-error inv-error" role="alert">{err}</div>}

      <div className="vava-card inv-activity-card" style={{ marginTop: 0 }}>
        {events === null ? (
          <div className="vava-hint">加载中…</div>
        ) : filtered.length === 0 ? (
          <div className="inv-empty">
            <div className="emoji">📭</div>
            <div className="title">没有匹配的活动</div>
            <div className="sub">
              {events.length === 0
                ? '区间内还没有库存记录。在「当日现货」录入第一条。'
                : '调整上方筛选，或清空搜索框看全部。'}
            </div>
          </div>
        ) : (
          <ul className="inv-feed">
            {filtered.map(e => <ActivityItem key={e.id} e={e} />)}
          </ul>
        )}
      </div>
    </>
  );
};
